"use client"

import type { ReactNode } from "react"

import { toneStyle } from "@/lib/stage-colors"
import { cn } from "@/lib/utils"

/**
 * The Graphite record frame, in one place.
 *
 * Every tab on the candidate record — résumé, events, outreach, placements,
 * comments — draws the same thing: one surface on the canvas, a hairline header
 * carrying a small muted label, and content below it. That frame was copied as
 * a `SURFACE` class constant into five files before it was extracted, which is
 * exactly how two of them drifted.
 *
 * The canvas rule these encode: a surface holds content, it does not nest more
 * surfaces inside itself. So a tab is one `TabSurface` with hairline-separated
 * rows in it, never a stack of cards.
 */

export const SURFACE_CLASS =
  "rounded-[10px] border bg-card px-4 py-4 @2xl:px-6 @2xl:py-5"

export function TabSurface({
  children,
  className,
  fill = true,
}: {
  children: ReactNode
  className?: string
  /** Fill the column, so a tab and the rail beside it end on the same line. */
  fill?: boolean
}) {
  return (
    <div
      className={cn(
        SURFACE_CLASS,
        fill && "flex min-h-full flex-col",
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * A tab's header: the label, whatever actions belong to it, and the hairline
 * that separates it from the body.
 */
export function TabHeader({
  label,
  count,
  actions,
  children,
}: {
  label: string
  /** Machine value, so it renders mono. */
  count?: number
  actions?: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="flex-shrink-0 border-b pb-3.5">
      <div className="flex items-center gap-2">
        <span className="text-[11.5px] font-medium text-muted-foreground">
          {label}
        </span>
        {count !== undefined && (
          <span className="font-mono text-[11.5px] text-muted-foreground">
            {count}
          </span>
        )}
        {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
      </div>
      {children}
    </div>
  )
}

/**
 * The record's secondary action — outlined in teal rather than filled, so it
 * reads as available without competing with the one primary action on screen.
 */
export function SecondaryAction({
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-primary/35 bg-primary/[0.07] px-3 text-[12px] font-medium text-brand-ink transition-colors hover:bg-primary/[0.12]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

/**
 * A quiet bordered action — neutral, for the things that are not the point.
 *
 * `md` exists for a dialog's footer, where this stands beside the filled
 * `PageAction tone="primary"` and two buttons of different heights read as a
 * mistake. Everywhere else the smaller one is right.
 *
 * `destructive` is for a delete that belongs on screen but is not what the
 * screen is for — outlined in the destructive hue rather than filled with it,
 * so it does not compete with the primary action beside it.
 */
export function QuietAction({
  children,
  className,
  size = "sm",
  tone = "neutral",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  size?: "sm" | "md"
  tone?: "neutral" | "destructive"
}) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border transition-colors",
        size === "md" ? "h-9 px-[13px] text-[12.5px]" : "h-8 px-3 text-[12px]",
        tone === "destructive"
          ? "border-destructive/30 text-destructive hover:bg-destructive/10"
          : "text-muted-foreground hover:text-foreground",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

/**
 * A tinted pill in a hue the canvas defines by value — pipeline stages (4b),
 * event types, anything else keyed to a colour rather than a token. The border,
 * fill and dot alphas are the canvas's: 59, 14, solid.
 *
 * `muted` drains it: the tint and the coloured text go, the border falls back
 * to the neutral one and the dot dims, while the pill keeps its size so a row
 * of them does not reflow as they are switched. That is the "off" half of a
 * pill used as a filter — the calendar's type legend — and it reads across a
 * row at a glance in a way that striking the word alone does not.
 */
export function TonePill({
  hex,
  children,
  compact,
  dot = true,
  muted,
}: {
  hex: string
  children: ReactNode
  compact?: boolean
  dot?: boolean
  muted?: boolean
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border capitalize",
        compact
          ? "gap-[5px] px-[9px] py-1 text-[11px]"
          : "gap-1.5 px-2.5 py-[5px] text-[11.5px]",
        muted && "border-border text-muted-foreground",
      )}
      style={muted ? undefined : toneStyle(hex)}
    >
      {dot && (
        /*
          `inline-block`, not a bare span: a span is `display: inline`, and an
          inline box ignores width and height. Where this pill is a direct flex
          child the browser blockifies it and the dot appears; put one wrapper
          in between and it silently lays out 0×0 and paints nothing, which is
          how the calendar legend shipped as four grey words.
        */
        <span
          className={cn(
            "inline-block shrink-0 rounded-full",
            compact ? "size-[5px]" : "size-1.5",
            muted && "opacity-40",
          )}
          style={{ backgroundColor: hex }}
        />
      )}
      {children}
    </span>
  )
}
